/**
 * Unified Conversations Hook
 * React Query hook for the "All" tab showing all conversation types
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  archiveConversation,
  getConnectionContext,
  listAllConversations,
  listArchivedConversations,
  unarchiveConversation,
} from "../services/unified-chat.service"

export function useAllConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["all-conversations", page, limit],
    queryFn: () => listAllConversations(page, limit),
    staleTime: 30 * 1000,
  })
}

export function useArchivedConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["archived-conversations", page, limit],
    queryFn: () => listArchivedConversations(page, limit),
    staleTime: 30 * 1000,
  })
}

export function useArchiveConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => archiveConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
    },
  })
}

export function useUnarchiveConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => unarchiveConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
    },
  })
}

export function useConnectionContext(userId: string | undefined) {
  return useQuery({
    queryKey: ["connection-context", userId],
    queryFn: () => getConnectionContext(userId as string),
    enabled: !!userId,
    staleTime: 60 * 1000,
  })
}
